import React, { useState, useMemo } from 'react'
import { Box, Card, Checkbox, Tag, TagLabel, TagLeftIcon, Text, Flex, Grid, IconButton, Tooltip, useToast } from '@chakra-ui/react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ExternalLink, UserPlus } from 'lucide-react'
import { CheckCircleFilledIcon, ClockFillIcon, XCircleIcon } from '../../assets/icons'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import MasterTable, { Column, RowData } from './base/MasterTable'
import SearchInput from './base/SearchInput'
import Pagination from './base/Pagination'
import LoadingTable from './base/LoadingTable'
import AvatarCell from './base/AvatarCell'
import AddEmployeesModal from '../modals/addEmployees'
import handleToast from '../HandleToast'
import { fetchInflowCandidates, patchInflowCandidate } from '../../api/recruitment/recruitment'
import { statusColors } from '../../themes/components/tag'
import { getInflowPageSetup } from '../../api/settings/pageSetup'

const DEFAULT_INFLOW_COLUMN_ORDER = [
  'name',
  'vacancy_title',
  'profile_completion',
  'it_access',
  'keys',
  'devices',
  'other_assets',
  'documents_status',
  'welcome_meeting_planned',
  'actions',
]

export default function InflowTable() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const toast = useToast()

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [showAddModal, setShowAddModal] = useState(false)
  const [prefillCandidate, setPrefillCandidate] = useState<any>(null)
  const itemsPerPage = 15

  const { data, isLoading } = useQuery({
    queryKey: ['inflow', page, search],
    queryFn: () => fetchInflowCandidates({ page, page_size: itemsPerPage, search: search.trim() || undefined }),
  })

  const { data: inflowPageSetup } = useQuery({
    queryKey: ['page-setup', 'inflow'],
    queryFn: getInflowPageSetup,
  })

  const candidates = data?.results ?? data?.data ?? []
  const totalCount = data?.count ?? 0
  const maxPage = Math.max(1, Math.ceil(totalCount / itemsPerPage))

  const toggleMutation = useMutation({
    mutationFn: ({ id, field, value }: { id: string; field: string; value: boolean }) =>
      patchInflowCandidate(id, { [field]: value }),
    onMutate: async ({ id, field, value }) => {
      await queryClient.cancelQueries({ queryKey: ['inflow'] })
      const prev = queryClient.getQueryData(['inflow', page, search])
      queryClient.setQueryData(['inflow', page, search], (old: any) => {
        if (!old) return old
        const key = old.results ? 'results' : 'data'
        return {
          ...old,
          [key]: old[key]?.map((c: any) => (c.id === id ? { ...c, [field]: value } : c)),
        }
      })
      return { prev }
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) queryClient.setQueryData(['inflow', page, search], context.prev)
      handleToast(
        toast,
        false,
        t('recruitment.inflow.messages.updateFailed'),
        t('recruitment.inflow.messages.updateFailedMessage'),
      )
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['inflow'] }),
  })

  const linkEmployeeMutation = useMutation({
    mutationFn: ({ candidateId, employeeId }: { candidateId: string; employeeId: string }) =>
      patchInflowCandidate(candidateId, { employee: employeeId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inflow'] })
      queryClient.invalidateQueries({ queryKey: ['people'] })
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      handleToast(
        toast,
        true,
        t('recruitment.inflow.messages.employeeCreatedSuccess'),
        t('recruitment.inflow.messages.employeeCreatedSuccessMessage'),
      )
      setShowAddModal(false)
      setPrefillCandidate(null)
    },
    onError: () => {
      handleToast(
        toast,
        false,
        t('recruitment.inflow.messages.linkFailed'),
        t('recruitment.inflow.messages.linkFailedMessage'),
      )
    },
  })

  const CheckboxCell = ({ row, field }: { row: any; field: string }) => (
    <Checkbox
      isChecked={!!row[field]}
      onChange={(e) => toggleMutation.mutate({ id: row.id, field, value: e.target.checked })}
      colorScheme="primary"
    />
  )

  const columnMap = useMemo<Record<string, Column>>(
    () => ({
      name: {
        label: t('common.fields.name'),
        accessor: 'name',
        type: 'custom',
        width: '16%',
        Cell: ({ row }: { row: RowData }) => (
          <AvatarCell
            name={`${row.first_name || ''} ${row.last_name || ''}`.trim()}
            subtitle={row.email}
          />
        ),
      },
      vacancy_title: {
        label: t('recruitment.inflow.columns.vacancy', 'Vacancy'),
        accessor: 'vacancy_title',
        type: 'custom',
        width: '10%',
        Cell: ({ row }: { row: RowData }) => (
          <Text textStyle="l-xs" isTruncated>{row.vacancy_title || '—'}</Text>
        ),
      },
      profile_completion: {
        label: t('recruitment.inflow.columns.profileData', 'Profile Data'),
        accessor: 'profile_completion',
        type: 'custom',
        width: '9%',
        Cell: ({ row }: { row: RowData }) => {
          const pc = row.profile_completion as { completed: number; total: number } | null
          if (!pc) {
            return (
              <Tooltip label={t('recruitment.inflow.noProfile', 'No employee profile linked')} placement="top" hasArrow>
                <Tag
                  borderRadius="14px"
                  h="28px"
                  color={statusColors.error.color}
                  bg={statusColors.error.bg}
                  borderColor={statusColors.error.border}
                  borderWidth="1px"
                  whiteSpace="nowrap"
                >
                  <TagLeftIcon><XCircleIcon /></TagLeftIcon>
                  <TagLabel textStyle="h-xxs">0</TagLabel>
                </Tag>
              </Tooltip>
            )
          }
          const missing = pc.total - pc.completed
          const isComplete = missing === 0
          const tooltipLabel = isComplete
            ? t('recruitment.inflow.profileComplete', 'Profile complete')
            : t('recruitment.inflow.profileMissing', { count: missing, defaultValue: '{{count}} fields missing' })
          return (
            <Tooltip label={tooltipLabel} placement="top" hasArrow>
              <Tag
                borderRadius="14px"
                h="28px"
                color={isComplete ? statusColors.success.color : statusColors.warning.color}
                bg={isComplete ? statusColors.success.bg : statusColors.warning.bg}
                borderColor={isComplete ? statusColors.success.border : statusColors.warning.border}
                borderWidth="1px"
                whiteSpace="nowrap"
              >
                {isComplete
                  ? <TagLeftIcon mr="0"><CheckCircleFilledIcon /></TagLeftIcon>
                  : <><TagLeftIcon><ClockFillIcon /></TagLeftIcon><TagLabel textStyle="h-xxs">{missing}</TagLabel></>
                }
              </Tag>
            </Tooltip>
          )
        },
      },
      it_access: {
        label: t('recruitment.inflow.columns.itAccess', 'IT Access'),
        accessor: 'it_access',
        type: 'custom',
        width: '7%',
        Cell: ({ row }: { row: RowData }) => <CheckboxCell row={row} field="it_access" />,
      },
      keys: {
        label: t('recruitment.inflow.columns.keys', 'Keys'),
        accessor: 'keys',
        type: 'custom',
        width: '6%',
        Cell: ({ row }: { row: RowData }) => <CheckboxCell row={row} field="keys" />,
      },
      devices: {
        label: t('recruitment.inflow.columns.devices', 'Devices'),
        accessor: 'devices',
        type: 'custom',
        width: '7%',
        Cell: ({ row }: { row: RowData }) => <CheckboxCell row={row} field="devices" />,
      },
      other_assets: {
        label: t('recruitment.inflow.columns.otherAssets', 'Other Assets'),
        accessor: 'other_assets',
        type: 'custom',
        width: '8%',
        Cell: ({ row }: { row: RowData }) => <CheckboxCell row={row} field="other_assets" />,
      },
      documents_status: {
        label: t('recruitment.inflow.columns.documents', 'Documents'),
        accessor: 'documents_status',
        type: 'custom',
        width: '9%',
        Cell: ({ row }: { row: RowData }) => {
          const doc = row.documents_status as { status: string; pending: number; total: number } | null
          const isPending = doc?.status === 'pending'
          const pending = doc?.pending ?? 0
          const tooltipLabel = !doc
            ? t('recruitment.inflow.noDocuments', 'No documents assigned')
            : isPending
              ? t('recruitment.inflow.documentsPending', { count: pending, defaultValue: '{{count}} documents pending' })
              : t('recruitment.inflow.allSigned', 'All documents signed')
          return (
            <Tooltip label={tooltipLabel} placement="top" hasArrow>
              <Tag
                borderRadius="14px"
                h="28px"
                color={isPending ? statusColors.warning.color : statusColors.success.color}
                bg={isPending ? statusColors.warning.bg : statusColors.success.bg}
                borderColor={isPending ? statusColors.warning.border : statusColors.success.border}
                borderWidth="1px"
                whiteSpace="nowrap"
              >
                {isPending
                  ? <><TagLeftIcon><ClockFillIcon /></TagLeftIcon><TagLabel textStyle="h-xxs">{pending}</TagLabel></>
                  : <><TagLeftIcon><CheckCircleFilledIcon /></TagLeftIcon><TagLabel textStyle="h-xxs">{doc?.total ?? 0}</TagLabel></>
                }
              </Tag>
            </Tooltip>
          )
        },
      },
      welcome_meeting_planned: {
        label: t('recruitment.inflow.columns.welcomeMeeting', 'Welcome Meeting'),
        accessor: 'welcome_meeting_planned',
        type: 'custom',
        width: '9%',
        Cell: ({ row }: { row: RowData }) => <CheckboxCell row={row} field="welcome_meeting_planned" />,
      },
      actions: {
        label: t('recruitment.inflow.columns.actions', 'Actions'),
        accessor: 'actions',
        type: 'custom',
        width: '8%',
        Cell: ({ row }: { row: RowData }) => {
          if (row.employee_id) {
            return (
              <Tooltip label={t('recruitment.inflow.actions.viewEmployee', 'View Employee')}>
                <IconButton
                  aria-label="View Employee"
                  icon={<ExternalLink size={16} />}
                  size="xs"
                  variant="ghost"
                  onClick={() => navigate(`/user/workforce/people/${row.employee_id}`)}
                />
              </Tooltip>
            )
          }
          return (
            <Tooltip label={t('recruitment.inflow.actions.createEmployee', 'Create Employee')}>
              <IconButton
                aria-label="Create Employee"
                icon={<UserPlus size={16} />}
                size="xs"
                variant="mono"
                isDisabled={linkEmployeeMutation.isPending}
                onClick={() => {
                  setPrefillCandidate(row)
                  setShowAddModal(true)
                }}
              />
            </Tooltip>
          )
        },
      },
    }),
    [t, navigate],
  )

  const columns: Column[] = useMemo(() => {
    const configuredColumns = inflowPageSetup?.config?.columns

    if (!configuredColumns?.length) {
      return DEFAULT_INFLOW_COLUMN_ORDER.map((key) => columnMap[key]).filter(Boolean)
    }

    return configuredColumns
      .filter((column: any) => column.visible)
      .map((column: any) => {
        const baseColumn = columnMap[column.key]
        if (!baseColumn) return null
        return {
          ...baseColumn,
          label: column.label || baseColumn.label,
        }
      })
      .filter(Boolean) as Column[]
  }, [columnMap, inflowPageSetup])

  const handleSearch = (term: string) => {
    setPage(1)
    setSearch(term)
  }

  const handleSelectRow = (id: string | number, isChecked: boolean) => {
    const strId = String(id)
    if (isChecked) {
      setSelectedRows((prev) => [...prev, strId])
    } else {
      setSelectedRows((prev) => prev.filter((rowId) => rowId !== strId))
    }
  }

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      setSelectedRows(candidates.map((row: RowData) => String(row.id)))
    } else {
      setSelectedRows([])
    }
  }

  return (
    <Card width="100%" p="0">
      <Grid>
        <Flex justifyContent="space-between" alignItems="center" height="64px" flexWrap="wrap" gap={4} minW={0} px="20px">
          <SearchInput onSearch={handleSearch} />
        </Flex>
        {isLoading ? (
          <LoadingTable columns={columns} />
        ) : (
          <Box minHeight="58vh" overflowX="auto" overflowY="visible">
            <MasterTable
              columns={columns}
              data={candidates}
              selectedRows={selectedRows}
              onSelectAll={handleSelectAll}
              onSelectRow={handleSelectRow}
              allSelected={candidates.length > 0 && selectedRows.length === candidates.length}
              isIndeterminate={candidates.length > 0 && selectedRows.length > 0 && selectedRows.length < candidates.length}
              tableType="people"
            />
          </Box>
        )}
        <Pagination
          currentPage={page}
          itemsPerPage={itemsPerPage}
          maxPage={maxPage}
          totalItems={totalCount}
          handlePreviousPage={() => setPage(page - 1)}
          handleNextPage={() => setPage(page + 1)}
          handlePageClick={(p: number) => setPage(p)}
        />
      </Grid>

      {showAddModal && (
        <AddEmployeesModal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false)
            setPrefillCandidate(null)
          }}
          prefillData={
            prefillCandidate
              ? {
                  first_name: prefillCandidate.first_name || '',
                  last_name: prefillCandidate.last_name || '',
                  work_email: prefillCandidate.email || '',
                  personal_email: prefillCandidate.email || '',
                  phone_number: prefillCandidate.phone || '',
                  country_of_work: prefillCandidate.country || '',
                }
              : undefined
          }
          onSuccess={(newEmployee: any) => {
            if (prefillCandidate?.id && newEmployee?.id) {
              linkEmployeeMutation.mutate({
                candidateId: prefillCandidate.id,
                employeeId: newEmployee.id,
              })
            }
          }}
        />
      )}
    </Card>
  )
}
